/**
 * The paper's questions, grouped by section.
 *
 * A section is how a paper says "Write before numbers" once at the top of a part
 * rather than repeating it on every question:
 *
 *     Write before numbers          <- section heading
 *       1.  10 ____
 *       2.  12 ____
 *     Write after numbers           <- next section
 *       3.  ____ 25
 *
 * Numbering runs on across sections, so the first question of the second part is
 * question 3 of the paper - which is what the answer key and the child both expect.
 * The number shown on each row is therefore its position in the *paper*, not in its
 * section.
 *
 * Sections only earn their chrome once there is more than one. A paper with a
 * single untitled section shows a plain list, because most worksheets are that and
 * a lone "Section 1" header would be noise.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../lib/api/client'
import type { Question, Section, WorksheetDetail } from '../../lib/api/client'
import { Button, ConfirmButton, IconButton, Input } from '../../components/ui'
import { Icon } from '../../components/icons'
import { QuestionRow } from './QuestionRow'
import { AddQuestion } from './AddQuestion'
import { subitemLabel } from '../../lib/labels'

export function SectionList({
  detail,
  totalMarks,
  editable,
  openId,
  onOpen,
  editorHeader,
  onSaved,
  onSaving,
  onFailed,
}: {
  detail: WorksheetDetail
  totalMarks?: number | null
  editable: boolean
  openId: string | null
  onOpen: (questionId: string | null) => void
  editorHeader?: ReactNode
  onSaved: () => void
  onSaving: () => void
  onFailed: (error: Error) => void
}) {
  const sections = detail.sections ?? []
  const run = (work: Promise<unknown>) => {
    onSaving()
    return work.then(onSaved).catch(onFailed)
  }

  const types = useQuery({ queryKey: ['question-types'], queryFn: api.questionTypes })
  const unnumbered = new Set(
    (types.data ?? []).filter((type) => !type.numbered).map((type) => type.type_key),
  )

  // Position in the *paper*, not in the section - the number a child reads. A
  // unnumbered content must not consume one here, or the row would disagree with
  // the printed page.
  let paperPosition = 0
  const numbering = new Map<string, string>()
  const passageNumbers = new Map<string, number>()
  const passageChildren = new Map<string, number>()
  for (const section of sections) {
    for (const question of section.questions) {
      const parentNumber = question.passage_id
        ? passageNumbers.get(question.passage_id)
        : undefined
      if (parentNumber !== undefined && question.passage_id) {
        const childNumber = (passageChildren.get(question.passage_id) ?? 0) + 1
        passageChildren.set(question.passage_id, childNumber)
        numbering.set(question.id, `${parentNumber}(${subitemLabel(childNumber - 1)})`)
        continue
      }

      if (!unnumbered.has(question.type)) paperPosition += 1
      numbering.set(question.id, String(paperPosition))
      if (question.type === 'passage') {
        passageNumbers.set(question.id, paperPosition)
        passageChildren.set(question.id, 0)
      }
    }
  }

  const many = sections.length > 1
  const questionCount = sections.reduce((total, section) => total + section.questions.length, 0)
  const selected = sections.flatMap((section) =>
    section.questions.map((question, index) => ({
      question,
      index,
      total: section.questions.length,
      paperNumber: numbering.get(question.id) ?? '0',
      hasPassageChildren:
        question.type === 'passage' &&
        section.questions.some((candidate) => candidate.passage_id === question.id),
    })),
  ).find((entry) => entry.question.id === openId)

  const addQuestion = (type: string, sectionId: string) => {
    onSaving()
    return api.addQuestion(detail.id, type, sectionId)
      .then((question) => {
        onOpen(question.id)
        onSaved()
      })
      .catch(onFailed)
  }

  return (
    <div className="tc-question-workspace">
      <aside className="tc-question-outline" aria-label="Question outline">
        <div className="tc-question-outline-head">
          <div>
            <span className="tc-field-label">Questions</span>
            <span className="tc-field-hint">
              {questionCount} {questionCount === 1 ? 'item' : 'items'}
            </span>
          </div>
          {detail.marks_enabled && totalMarks != null && (
            <span className="tc-total-marks-badge" aria-label={`Total ${formatMarks(totalMarks)}`}>
              <span>Total</span>
              <strong>{formatMarks(totalMarks)}</strong>
            </span>
          )}
        </div>

        <div className="tc-question-outline-scroll">
          {sections.map((section, sectionIndex) => (
            <div key={section.id} className={many ? 'tc-section' : undefined}>
              {many && (
                <SectionHeading
                  section={section}
                  index={sectionIndex}
                  total={sections.length}
                  worksheetId={detail.id}
                  editable={editable}
                  onRun={run}
                />
              )}

              <div className="tc-questions">
                {section.questions.length === 0 ? (
                  <p className="tc-field-hint tc-question-empty">
                    {many ? 'No questions in this part yet.' : 'No questions yet.'}
                  </p>
                ) : (
                  section.questions.map((question: Question, index: number) => (
                    <QuestionRow
                      key={question.id}
                      worksheetId={detail.id}
                      question={question}
                      index={index}
                      paperNumber={numbering.get(question.id) ?? '0'}
                      total={section.questions.length}
                      hasPassageChildren={
                        question.type === 'passage' &&
                        section.questions.some((candidate) => candidate.passage_id === question.id)
                      }
                      problems={detail.problems[question.id] ?? []}
                      marksEnabled={detail.marks_enabled}
                      editable={editable}
                      open={openId === question.id}
                      variant="outline"
                      onToggle={() => onOpen(question.id)}
                      onSaved={onSaved}
                      onSaving={onSaving}
                      onFailed={onFailed}
                    />
                  ))
                )}
              </div>

              {editable && (
                <AddQuestion
                  onPick={(type) => addQuestion(type, section.id)}
                  compact={many}
                />
              )}
            </div>
          ))}

          {editable && <AddSection worksheetId={detail.id} onRun={run} />}
        </div>
      </aside>

      <section className="tc-question-editor" aria-label="Question editor">
        <div className="tc-question-editor-scroll">
          {editorHeader}
          {selected ? (
            <QuestionRow
              key={selected.question.id}
              worksheetId={detail.id}
              question={selected.question}
              index={selected.index}
              paperNumber={selected.paperNumber}
              total={selected.total}
              hasPassageChildren={selected.hasPassageChildren}
              problems={detail.problems[selected.question.id] ?? []}
              marksEnabled={detail.marks_enabled}
              editable={editable}
              open
              variant="editor"
              onToggle={() => undefined}
              onSaved={onSaved}
              onSaving={onSaving}
              onFailed={onFailed}
            />
          ) : (
            <div className="tc-question-editor-empty">
              <span className="tc-question-editor-empty-icon">
                <Icon.papers size={24} />
              </span>
              <h2>{questionCount ? 'Choose a question to edit' : 'Add your first question'}</h2>
              <p>
                {questionCount
                  ? 'Select a question from the outline. Its full editor will open here.'
                  : 'Use Add question in the outline to choose from every available question type.'}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  )
}

function formatMarks(marks: number) {
  return `${marks} mark${marks === 1 ? '' : 's'}`
}

function SectionHeading({
  section,
  index,
  total,
  worksheetId,
  editable,
  onRun,
}: {
  section: Section
  index: number
  total: number
  worksheetId: string
  editable: boolean
  onRun: (work: Promise<unknown>) => void
}) {
  const [title, setTitle] = useState(section.title)
  useEffect(() => setTitle(section.title), [section.title])

  if (!editable) {
    return <h3 className="tc-section-head">{section.title || `Part ${index + 1}`}</h3>
  }

  return (
    <div className="tc-section-head">
      <Input
        aria-label={`Heading for part ${index + 1}`}
        value={title}
        placeholder={`Part ${index + 1} heading — for example: Write before numbers`}
        onChange={(event) => setTitle(event.target.value)}
        onBlur={() => onRun(api.updateSection(worksheetId, section.id, { title }))}
      />
      <IconButton
        label="Move this part up"
        size="sm"
        variant="ghost"
        disabled={index === 0}
        onClick={() => onRun(api.moveSection(worksheetId, section.id, index))}
      >
        <Icon.up size={15} />
      </IconButton>
      <IconButton
        label="Move this part down"
        size="sm"
        variant="ghost"
        disabled={index === total - 1}
        onClick={() => onRun(api.moveSection(worksheetId, section.id, index + 2))}
      >
        <Icon.down size={15} />
      </IconButton>
      <ConfirmButton
        label="Delete part"
        message="This part and the questions in it will be removed."
        onConfirm={() => onRun(api.deleteSection(worksheetId, section.id))}
      />
    </div>
  )
}

function AddSection({
  worksheetId,
  onRun,
}: {
  worksheetId: string
  onRun: (work: Promise<unknown>) => void
}) {
  return (
    <Button
      size="sm"
      icon={<Icon.plus size={14} />}
      className="tc-add-section"
      onClick={() => onRun(api.addSection(worksheetId))}
      title="A new part with its own heading, for example: Write after numbers"
    >
      Add a new part
    </Button>
  )
}
